import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaClipboardCheck, FaFileLines, FaListCheck, FaCalendarDays, FaFolderOpen } from "react-icons/fa6";
import PageHero from "../components/PageHero";

const STEPS = [
  { icon: <FaClipboardCheck />, t: "Accreditation process overview", d: "Map the standards, the cycle, and the bodies that matter for your program. Build shared understanding across leadership and faculty." },
  { icon: <FaFileLines />, t: "Writing effective self-study reports", d: "Structure narratives that clearly demonstrate compliance, learning outcomes, and continuous improvement — without burying the evidence." },
  { icon: <FaListCheck />, t: "Preparing for accreditation visits", d: "Brief your team, rehearse interviews, and organize documents and tours so reviewers can see your program at its best." },
  { icon: <FaCalendarDays />, t: "Accreditation readiness planning", d: "Build a multi-month timeline that distributes work, reduces last-minute scrambling, and protects faculty time." },
  { icon: <FaFolderOpen />, t: "Evidence organization", d: "Create a defensible, navigable evidence base — and keep it usable year over year, not just at cycle time." },
];

export default function Accreditation() {
  useEffect(() => {
    document.title = 'Accreditation Preparation — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);
  return (
    <div className="w-full">
      <PageHero
        eyebrow="Accreditation Preparation"
        title={
          <>
            Confident, structured preparation —{" "}
            <span className="italic text-brand-moss">built for institutional realities.</span>
          </>
        }
        lead="Whether your program is approaching a first accreditation cycle or improving an established one, we help you organize evidence, sharpen your self-study, and arrive at the visit ready."
      />

      {/* Steps */}
      <section className="bg-bone">
        <div className="container-page py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-5">
              <div className="eyebrow eyebrow-bar">Where we help</div>
              <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish text-ink">
                A clear path through the work that accreditation actually requires.
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-slate2 leading-relaxed text-lg">
                We work alongside program leads, faculty, and accreditation
                coordinators. Engagements range from short consultations on a single
                standard to multi-month readiness programs across a full self-study.
              </p>
            </div>
          </div>

          <ul className="space-y-3">
            {STEPS.map((s) => (
              <li
                key={s.t}
                className="flex items-start gap-5 rounded-2xl border border-ink/10 bg-white p-6 hover:border-ink/30 transition"
              >
                <div className="text-brand-deep text-xl shrink-0 mt-1">
                  {s.icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl text-ink">{s.t}</h3>
                  <p className="mt-1 text-slate2 leading-relaxed">{s.d}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Courses & tools in development */}
      <section className="bg-cream">
        <div className="container-page py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow eyebrow-bar">In Development</div>
            <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish text-ink">
              Dedicated accreditation courses and digital tools — coming soon.
            </h2>
            <p className="mt-4 text-slate2 leading-relaxed text-lg">
              Alongside our consulting work, we are quietly developing focused
              courses and digital tools to support accreditation teams through every
              stage of the cycle. We will share more as each becomes available — let
              us know if you would like to be notified.
            </p>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Link to="/contact" className="btn-primary">
              Get notified <FaArrowRight className="text-xs" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-bone">
        <div className="container-page pb-20 pt-16">
          <div className="rounded-3xl bg-ink text-bone p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <div className="eyebrow !text-brand-sage">
                <span className="inline-block h-px w-8 bg-brand-sage mr-2 align-middle" />
                Plan your readiness
              </div>
              <h3 className="mt-3 font-display text-2xl lg:text-3xl leading-tight">
                Map your accreditation cycle with us.
              </h3>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-bone text-ink font-medium px-6 py-3 rounded-full hover:bg-cream transition-colors"
            >
              Schedule a consultation <FaArrowRight className="text-xs" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
